// What a trade looks like to a reader, what the league's chat says about it,
// and how a seat with no person in it answers one. Pure, and host-agnostic:
// the simulator (`packages/sim/src/trades.ts`) and the league store
// (`packages/league/src/trades.ts`) each supply a `TradeHost` over their own
// data and get the same views, the same sentences and the same answers — one
// copy, so a practice league and a real one cannot drift (ICD-A01's
// direction, the way `@fsp/waivers` holds every waiver sentence).
//
// Moved here from the simulator on 2026-09-26 (PLAN-2026-09-26-trades T2),
// unchanged in behavior: the simulator's tests are the proof.

import {
  formatInZone,
  type DraftPickRef,
  type DropOption,
  type LeagueRef,
  type PlayerSummary,
  type TeamRef,
  type TradeBlockEntry,
  type TradeBuilderView,
  type TradeReadoutView,
  type TradeRosterRow,
  type TradeSideView,
  type RosterDepthView,
  type TradesView,
  type TradeView,
} from '@fsp/domain';
import { rosterDepth } from './depth';
import {
  closedReason,
  isOpen,
  overLimits,
  proposalProblem,
  sideNames,
  sideProblem,
  statusSentence,
  vetoesNeeded,
} from './engine';
import { readout } from './readout';
import type {
  Trade,
  TradeBook,
  TradeContext,
  TradeEvent,
  TradeIntent,
  TradeReadout,
  ValueContext,
} from './types';

const MIN = 60 * 1000;
const H = 60 * MIN;
const DAY = 24 * H;

/** Offers expire after three days unanswered: product behavior (TRADES.md), not a rule. */
export const OFFER_TTL_MS = 3 * DAY;

/** What a host knows that the views need. Every name and time is the host's. */
export interface TradeHost {
  now: number;
  /** The reader's team; null for somebody with no team in the league. */
  myTeamId: string | null;
  book: TradeBook;
  ctx: TradeContext;
  value: ValueContext;
  rosterOf: (teamId: string) => readonly string[];
  teamRef: (teamId: string) => TeamRef;
  /** The manager, the seat's bot, or "Former manager". */
  ownerName: (teamId: string) => string;
  row: (teamId: string, playerId: string) => TradeRosterRow;
  summary: (playerId: string) => PlayerSummary;
  /** A time in the reader's zone, named (B-181): "Wed 9:00 PM MDT". */
  when: (ms: number) => string;
  commissionerName: string;
  /** The reader casts the team's vote: its manager, not a co-manager (B-435). */
  castsVote: boolean;
  /** The reader's team is out of an elimination league (Q1): it proposes and accepts nothing. */
  myTeamOut: boolean;
  /** The teams out of an elimination league, which are offered nothing. */
  outTeamIds: ReadonlySet<string>;
  /**
   * Why the league trades nothing at all now — before the draft, after the
   * season — in the host's words, or null when it trades. The store refuses
   * every answer then, so no action is offered.
   */
  leagueClosed: string | null;
  /**
   * A draft pick, named and placed (PLAN-2026-10-27-the-next-season §2.6),
   * and the picks a team holds that the league trades now. Absent: a host
   * whose league trades no picks, as the practice league's.
   */
  pickRef?: (pickId: string) => DraftPickRef;
  picksOf?: (teamId: string) => readonly DraftPickRef[];
}

const OUT = 'Your team is out of this league, so it has nothing to trade.';

/** Why the reader can trade nothing now, or null: the league's close, the team's, the rules'. */
function closedForReader(host: TradeHost): string | null {
  return host.leagueClosed ?? (host.myTeamOut ? OUT : null) ?? closedReason(host.ctx);
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

// ── Views ─────────────────────────────────────────────────────────────────

function sideView(host: TradeHost, trade: Trade, index: 0 | 1): TradeSideView {
  const s = trade.sides[index];
  return {
    team: host.teamRef(s.teamId),
    players: s.playerIds.map((id) => host.row(s.teamId, id)),
    drops: s.dropPlayerIds.map((id) => host.summary(id)),
    faab: s.faab,
    picks: [...s.pickIds],
    draftPicks: host.pickRef === undefined ? [] : s.pickIds.map((id) => host.pickRef!(id)),
  };
}

/**
 * The read-out from one side's point of view, spoken to that side, or about
 * it by name for a reader in neither (`named`, B-389).
 *
 * A TRADE THAT HAS EXECUTED is read with the trade taken back out of each
 * roster: the roster now, less what it received and anybody the other side
 * dropped in it (claimed since), with what it gave and dropped back. Read
 * against today's rosters, its players were already on both sides of it, and
 * every line said "the same" (B-389). A move made since is in the roster
 * too: this is the trade as it would land now, not a record of the day.
 */
export function readoutFor(
  host: Pick<TradeHost, 'rosterOf' | 'value'>,
  trade: Trade,
  perspective: string,
  named = false,
): TradeReadoutView {
  const sides = sidesOf(host, trade, perspective);
  const r = readout(sides, host.value, named);
  return { ...r, perspectiveTeamId: sides.mine.teamId };
}

/** Each side of a trade as the read-out weighs it, `perspective`'s first. */
function sidesOf(
  host: Pick<TradeHost, 'rosterOf'>,
  trade: Trade,
  perspective: string,
): Record<
  'mine' | 'theirs',
  {
    teamId: string;
    roster: string[];
    give: readonly string[];
    drops: readonly string[];
    givePicks: readonly string[];
  }
> {
  const mineSide = trade.sides.find((s) => s.teamId === perspective) ?? trade.sides[0];
  const theirSide = trade.sides.find((s) => s !== mineSide)!;
  const before = (side: typeof mineSide, other: typeof mineSide): string[] => {
    const now = host.rosterOf(side.teamId);
    if (trade.status !== 'executed') return [...now];
    const back = [...side.playerIds, ...side.dropPlayerIds].filter((id) => !now.includes(id));
    const theirs = new Set([...other.playerIds, ...other.dropPlayerIds]);
    return [...now.filter((id) => !theirs.has(id)), ...back];
  };
  return {
    mine: {
      teamId: mineSide.teamId,
      roster: before(mineSide, theirSide),
      give: mineSide.playerIds,
      drops: mineSide.dropPlayerIds,
      givePicks: mineSide.pickIds,
    },
    theirs: {
      teamId: theirSide.teamId,
      roster: before(theirSide, mineSide),
      give: theirSide.playerIds,
      drops: theirSide.dropPlayerIds,
      givePicks: theirSide.pickIds,
    },
  };
}

/**
 * `perspective`'s best lineup before and after a trade, with the wire's best
 * standing in any slot he beats (`ValueContext.bestLineup`'s `filled`): the
 * second count a bot answering an offer weighs its lineup's gain by
 * (`botAnswer`'s `filled`), beside the read-out's, as its own offers weigh
 * the other side (`planTrade`). The rosters are the read-out's.
 */
export function filledLineupFor(
  host: Pick<TradeHost, 'rosterOf' | 'value'>,
  trade: Trade,
  perspective: string,
): { before: number; after: number } {
  const { mine, theirs } = sidesOf(host, trade, perspective);
  const after = [
    ...mine.roster.filter((id) => !mine.give.includes(id) && !mine.drops.includes(id)),
    ...theirs.give,
  ];
  return {
    before: host.value.bestLineup(mine.roster, true),
    after: host.value.bestLineup(after, true),
  };
}

/** What the reader may do to this trade now. */
export function actionsFor(host: TradeHost, trade: Trade): TradeView['actions'] {
  const me = host.myTeamId;
  const actions: TradeView['actions'] = [];
  if (me === null) return actions;
  const iAmIn = trade.sides.some((s) => s.teamId === me);
  const isProposer = trade.proposerTeamId === me;
  // A LEAGUE THAT TRADES NOTHING NOW takes no answer at all (`writer`).
  if (host.leagueClosed !== null) return actions;
  const commish = host.ctx.reviewerTeamIds.includes(me);
  // ONLY WHAT THE SERVER WILL TAKE: with trading closed (the deadline, the
  // playoffs), the reader's team out, or the other side's — knocked out
  // since it offered — an offer can be turned down and nothing else.
  const otherOut = trade.sides.some((s) => s.teamId !== me && host.outTeamIds.has(s.teamId));
  const shut = closedForReader(host) !== null || otherOut;
  if (trade.status === 'proposed' && iAmIn && !isProposer)
    actions.push(...(shut ? (['decline'] as const) : (['accept', 'counter', 'decline'] as const)));
  if (trade.status === 'proposed' && isProposer) actions.push('withdraw');
  if (
    trade.status === 'review' &&
    host.ctx.rules.review === 'league-vote' &&
    !iAmIn &&
    host.castsVote &&
    !trade.votes.some((v) => v.teamId === me)
  )
    actions.push('vote');
  if ((trade.status === 'review' || trade.status === 'accepted') && commish)
    actions.push('approve', 'veto');
  if (trade.status === 'accepted' && commish) actions.splice(actions.indexOf('approve'), 1);
  return actions;
}

export function tradeView(host: TradeHost, trade: Trade): TradeView {
  const { ctx, now } = host;
  const me = host.myTeamId;
  const iAmIn = me !== null && trade.sides.some((s) => s.teamId === me);
  const isProposer = me !== null && trade.proposerTeamId === me;
  const actions = actionsFor(host, trade);
  const vetoes = trade.votes.filter((v) => v.veto).length;
  const receiver = trade.sides[1];
  const needsDrops =
    iAmIn && !isProposer && trade.status === 'proposed'
      ? sideProblem(ctx, trade, receiver.teamId)
      : null;
  const thread: TradeView['thread'] = [];
  let cur: Trade | undefined = trade;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    // ONLY THE OFFERS THIS READER MAY SEE (the audit of 2026-10-20): a
    // countered offer stays its two managers' and the commissioner's, notes
    // and all, after the counter that answered it is accepted. Until then an
    // accepted counter showed every member the offers before it.
    if (cur !== trade && !visibleTo(host, cur)) {
      cur = cur.counterOf ? host.book.trades.find((t) => t.id === cur!.counterOf) : undefined;
      continue;
    }
    thread.unshift({
      tradeId: cur.id,
      at: iso(cur.createdAt),
      by: host.ownerName(cur.proposerTeamId),
      summary: statusSentence(cur, ctx),
      note: cur.note,
    });
    cur = cur.counterOf ? host.book.trades.find((t) => t.id === cur!.counterOf) : undefined;
  }
  // Never "0 days": under a day left reads as one, and says so in the singular.
  const days = Math.max(1, Math.round((trade.respondBy - now) / DAY));
  const timeLabel =
    trade.status === 'proposed'
      ? `${days} day${days === 1 ? '' : 's'} to answer`
      : trade.status === 'review' && trade.reviewEndsAt !== null
        ? `Review ends ${host.when(trade.reviewEndsAt)}`
        : trade.status === 'accepted' && trade.executeAt !== null
          ? `Executes ${host.when(trade.executeAt)}`
          : host.when(trade.resolution?.at ?? trade.createdAt);
  return {
    id: trade.id,
    status: trade.status,
    sentence: statusSentence(trade, ctx),
    proposer: host.teamRef(trade.proposerTeamId),
    sides: [sideView(host, trade, 0), sideView(host, trade, 1)],
    isMine: iAmIn,
    isProposer,
    note: trade.note,
    createdAt: iso(trade.createdAt),
    respondBy: iso(trade.respondBy),
    reviewEndsAt: trade.reviewEndsAt === null ? null : iso(trade.reviewEndsAt),
    executeAt: trade.executeAt === null ? null : iso(trade.executeAt),
    counterOf: trade.counterOf,
    counteredBy: trade.counteredBy,
    votes: {
      vetoes,
      needed: vetoesNeeded(ctx, trade),
      iVoted: me !== null && trade.votes.some((v) => v.teamId === me),
      canVote: actions.includes('vote'),
    },
    actions,
    readout: readoutFor(
      host,
      trade,
      iAmIn && me !== null ? me : trade.proposerTeamId,
      !(iAmIn && me !== null),
    ),
    needsDropsOnAccept: needsDrops,
    thread,
    timeLabel,
  };
}

/**
 * The review rule in one line, for the Trades header, and in a league with a
 * bot in it the trades it leaves out (B-481).
 */
export function reviewRuleSentence(host: Pick<TradeHost, 'ctx' | 'commissionerName'>): string {
  const rules = host.ctx.rules;
  if (rules.review === 'none') return 'Accepted trades go through right away.';
  const rule =
    rules.review === 'commissioner'
      ? `${host.commissionerName} has ${rules.reviewWindowHours} hours to review an accepted trade.`
      : `The league has ${rules.reviewWindowHours} hours to vote an accepted trade down.`;
  return (host.ctx.bots?.size ?? 0) > 0
    ? `${rule} A trade with a bot goes through once it is agreed.`
    : rule;
}

/**
 * Whether a trade was ever accepted: it went to review, or it cleared to
 * execute. What makes an offer the league's business (B-184), whatever it
 * became after — a trade voided once it cleared with no review was still
 * accepted, and the chat said so (the trade review's finding 15).
 */
export function wasAccepted(trade: Trade): boolean {
  return trade.reviewEndsAt !== null || trade.executeAt !== null;
}

/**
 * Who may see a trade (TRADES.md "The trade lifecycle"): an offer is its two
 * managers' and the commissioner's business until it is accepted; from then
 * on, and once it closes after an acceptance, the league's. A declined,
 * withdrawn or expired offer stays private, so a rejected offer is not gossip.
 */
export function visibleTo(host: Pick<TradeHost, 'myTeamId' | 'ctx'>, trade: Trade): boolean {
  const me = host.myTeamId;
  if (me !== null && trade.sides.some((s) => s.teamId === me)) return true;
  if (me !== null && host.ctx.reviewerTeamIds.includes(me)) return true;
  return (
    trade.status === 'review' ||
    trade.status === 'accepted' ||
    trade.status === 'executed' ||
    trade.status === 'vetoed' ||
    (trade.status === 'void' && wasAccepted(trade))
  );
}

/**
 * Each roster's depth by position and the teams that fit mine (B-388,
 * `rosterDepth`), from every player's season on the host's rows. Null while
 * no rostered player has a game.
 */
export function depthView(host: TradeHost): RosterDepthView | null {
  const slots = host.ctx.roster.slots
    .filter((s) => s.kind === 'starter' && s.eligible.length === 1)
    .map((s) => ({ position: s.eligible[0]!, count: s.count }));
  const depth = rosterDepth({
    slots,
    myTeamId: host.myTeamId,
    teams: host.ctx.teamIds.map((teamId) => ({
      teamId,
      players: host.rosterOf(teamId).map((playerId) => ({
        playerId,
        position: host.value.positionOf(playerId),
        avg: host.row(teamId, playerId).seasonAvg,
      })),
    })),
  });
  if (depth === null) return null;
  return {
    positions: depth.positions,
    teams: depth.teams.map((t) => ({ team: host.teamRef(t.teamId), held: t.held })),
    partners: depth.partners,
  };
}

export function tradesView(
  host: TradeHost,
  input: {
    league: LeagueRef;
    block: TradeBlockEntry[];
  },
): TradesView {
  const all = host.book.trades.filter((t) => visibleTo(host, t)).map((t) => tradeView(host, t));
  const rules = host.ctx.rules;
  const closed = closedForReader(host);
  const newest = (a: TradeView, b: TradeView) => b.createdAt.localeCompare(a.createdAt);
  const open = closed === null;
  return {
    league: input.league,
    header: {
      deadlinePeriod: rules.deadlinePeriod,
      deadlineLabel:
        rules.deadlinePeriod === null
          ? 'No trade deadline'
          : `Deadline: end of Week ${rules.deadlinePeriod}`,
      reviewRule: reviewRuleSentence(host),
      open,
      closedReason: closed,
    },
    forMe: all.filter((t) => t.status === 'proposed' && t.isMine && !t.isProposer).sort(newest),
    sent: all.filter((t) => t.status === 'proposed' && t.isProposer).sort(newest),
    review: all.filter((t) => t.status === 'review' || t.status === 'accepted').sort(newest),
    history: all.filter((t) => !isOpen(host.book.trades.find((x) => x.id === t.id)!)).sort(newest),
    block: host.leagueClosed === null ? input.block : [],
    depth: open ? depthView(host) : null,
    // THE PICKS THAT HAVE CHANGED HANDS, every team's (§2.6): who holds whose
    // is the league's business, as Sleeper's board shows it.
    picksOpen: host.picksOf !== undefined && host.ctx.picks !== undefined && open,
    picks:
      host.picksOf === undefined
        ? []
        : host.ctx.teamIds
            .flatMap((id) => host.picksOf!(id))
            .filter((p) => p.holder !== null && p.holder.id !== p.team.id)
            .sort(
              (a, b) => a.season - b.season || a.round - b.round || a.name.localeCompare(b.name),
            ),
    myTeamId: host.myTeamId ?? '',
    updatedAt: iso(host.now),
  };
}

export interface BuilderInput {
  partnerTeamId: string | null;
  give: string[];
  get: string[];
  /** Draft picks each way (§2.6). Absent: none. */
  givePicks?: string[];
  getPicks?: string[];
  myDrops: string[];
  counterOf: string | null;
}

export function tradeBuilderView(
  host: TradeHost,
  input: BuilderInput,
  dropOption: (playerId: string, droppable: boolean) => DropOption,
): TradeBuilderView {
  const { ctx } = host;
  const me = host.myTeamId!;
  const mineRoster = host.rosterOf(me);
  // A TEAM OUT OF AN ELIMINATION LEAGUE is no partner: its roster went to
  // waivers, and the server refuses an offer to it (Q1).
  const partnerId =
    input.partnerTeamId !== null &&
    input.partnerTeamId !== me &&
    ctx.teamIds.includes(input.partnerTeamId) &&
    !host.outTeamIds.has(input.partnerTeamId)
      ? input.partnerTeamId
      : null;
  const theirRoster = partnerId === null ? [] : host.rosterOf(partnerId);
  const openWith = (teamId: string) =>
    host.book.trades.filter(
      (tr) =>
        tr.status === 'proposed' &&
        tr.sides.some((s) => s.teamId === teamId) &&
        tr.sides.some((s) => s.teamId === me),
    ).length;
  const partners = ctx.teamIds
    .filter((id) => id !== me && !host.outTeamIds.has(id))
    .map((id) => ({ team: host.teamRef(id), openOffers: openWith(id) }));
  const give = input.give.filter((id) => mineRoster.includes(id));
  const get = input.get.filter((id) => theirRoster.includes(id));
  // THE PICKS EACH SIDE HOLDS that the league trades now (§2.6), and only
  // those of them checked.
  const myPicks = host.picksOf?.(me) ?? [];
  const theirPicks = partnerId === null ? [] : (host.picksOf?.(partnerId) ?? []);
  const givePicks = (input.givePicks ?? []).filter((id) => myPicks.some((p) => p.id === id));
  const getPicks = (input.getPicks ?? []).filter((id) => theirPicks.some((p) => p.id === id));
  const anything = give.length + get.length + givePicks.length + getPicks.length > 0;
  // WHETHER A SIDE NEEDS A DROP is asked of its roster after the swap and
  // before any drop: over its size or a position's limit (`overLimits`).
  // Asked after the drops picked, the picker vanished as soon as enough were
  // picked, and the screen went on sending drops it no longer showed — and
  // one picked and then traded was refused as malformed (the trade review of
  // 2026-10-22, production's two 400s).
  const mineNeedsDrop = overLimits(ctx, me, [
    ...mineRoster.filter((id) => !give.includes(id)),
    ...get,
  ]);
  const theirsNeedsDrop =
    partnerId !== null &&
    overLimits(ctx, partnerId, [...theirRoster.filter((id) => !get.includes(id)), ...give]);
  // The drops this offer carries: none when none is needed.
  const myDrops = mineNeedsDrop
    ? input.myDrops.filter((id) => mineRoster.includes(id) && !give.includes(id))
    : [];
  const problem =
    partnerId !== null
      ? proposalProblem(ctx, { by: me, to: partnerId, give, get, givePicks, getPicks, myDrops })
      : anything
        ? 'Pick a team to trade with.'
        : null;
  const readoutValue =
    partnerId !== null && anything
      ? {
          ...readout(
            {
              mine: { teamId: me, roster: mineRoster, give, drops: myDrops, givePicks },
              theirs: {
                teamId: partnerId,
                roster: theirRoster,
                give: get,
                drops: [],
                givePicks: getPicks,
              },
            },
            host.value,
          ),
          perspectiveTeamId: me,
        }
      : null;
  const byLineup = (a: TradeRosterRow, b: TradeRosterRow) =>
    Number(b.starting) - Number(a.starting) || (b.projection ?? 0) - (a.projection ?? 0);
  return {
    partner: partnerId === null ? null : host.teamRef(partnerId),
    partners,
    mine: mineRoster.map((id) => host.row(me, id)).sort(byLineup),
    theirs:
      partnerId === null ? [] : theirRoster.map((id) => host.row(partnerId, id)).sort(byLineup),
    give,
    get,
    myPicks: [...myPicks],
    theirPicks: [...theirPicks],
    givePicks,
    getPicks,
    myDrops,
    maxPerSide: ctx.rules.maxPlayersPerSide,
    readout: readoutValue,
    mineNeedsDrop,
    theirsNeedsDrop,
    myDropOptions: mineRoster
      .filter((id) => !give.includes(id))
      .map((id) => dropOption(id, !give.includes(id)))
      .sort((a, b) => (a.projection ?? Infinity) - (b.projection ?? Infinity)),
    problem,
    counterOf: input.counterOf,
    closedReason: closedForReader(host),
  };
}

// ── The league's chat ───────────────────────────────────────────────────────

/**
 * The line a trade event posts to the league's chat, or null for an event
 * that posts nothing (a withdrawal, a vote, an approval — the execution that
 * follows an approval says it). Stored in the league's chat, which every
 * manager reads, so a time is the league's zone, named: no one reader's zone
 * is everybody's (B-181). The players of an offer are not posted until it is
 * accepted, so a rejected offer is not league gossip (TRADES.md "Send").
 */
export function tradeChatLine(
  e: TradeEvent,
  names: {
    ownerName: (teamId: string) => string;
    playerName: (playerId: string) => string;
    /** A draft pick's name (§2.6 rule 8). Absent: a host whose league trades none. */
    pickName?: (pickId: string) => string;
    commissionerName: string;
    leagueZone: string;
    review: TradeContext['rules']['review'];
  },
): string | null {
  if (e.type === 'rejected') return null;
  const [a, b] = e.trade.sides;
  const who = names.ownerName;
  const deal = `${who(a.teamId)} sends ${sideNames(a, names)} to ${who(b.teamId)} for ${sideNames(b, names)}`;
  switch (e.type) {
    case 'proposed':
      return `${who(a.teamId)} offered ${who(b.teamId)} a trade.`;
    case 'countered':
      return `${who(a.teamId)} countered ${who(b.teamId)}'s offer.`;
    case 'review.started': {
      const until = formatInZone(e.endsAt, names.leagueZone, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      });
      return `Trade accepted: ${deal}. ${names.review === 'league-vote' ? `The league has until ${until} to object.` : `${names.commissionerName} has until ${until} to review it.`}`;
    }
    case 'accepted':
      return `Trade accepted: ${deal}. It executes after this week's games.`;
    case 'vetoed':
      return e.by === 'commissioner'
        ? `Trade vetoed by ${names.commissionerName}: "${e.reason ?? ''}"`
        : (e.reason ?? 'Trade vetoed by league vote.');
    case 'executed':
      return `Trade done: ${deal}.`;
    case 'declined':
      return `${who(b.teamId)} declined ${who(a.teamId)}'s offer.`;
    case 'expired':
      return `${who(a.teamId)}'s offer to ${who(b.teamId)} expired.`;
    // AN OFFER NOBODY ACCEPTED STAYS BETWEEN ITS TWO MANAGERS (B-184): its
    // void says whose offer it was, and names no player, where the reason
    // (*"Kaleb Johnson was traded in another deal."*) would.
    case 'void':
      return wasAccepted(e.trade)
        ? `Trade void: ${e.reason}`
        : `${who(a.teamId)}'s offer to ${who(b.teamId)} is void.`;
    case 'withdrawn':
    case 'voted':
    case 'approved':
      return null;
  }
}

// ── A seat with no person in it ─────────────────────────────────────────────

/**
 * How a manager with no person behind the seat answers an offer made to it,
 * by its temperament (A4, its `BotStyle`): accept when the deal gains its
 * team its bar, counter by asking for the proposer's player worth the least
 * that closes the gap when it haggles, falls short by no more than its
 * haggling reaches and the offer is not itself a counter, decline otherwise.
 * The same offer always gets the same answer from the same bot, so a manager
 * can learn which bot to deal with. `rand` is the host's seeded generator,
 * drawn once, for how long the answer takes (`answerDelayMs`, fifteen minutes
 * to an hour): a bot never answers in the same breath as the offer.
 */
export function botAnswer(input: {
  trade: Trade;
  /** The read-out from the answering side's point of view. */
  readout: TradeReadout;
  /**
   * Its own best lineup before and after, with the wire's best in any slot he
   * beats (`filledLineupFor`): its lineup's gain is the smaller of this one's
   * and the read-out's, which counts an empty slot as nothing (B-294).
   */
  filled: { before: number; after: number };
  /**
   * What the trade must gain its team before it says yes, as a share of the
   * deal's size — the larger side's worth (`BotStyle.answerBar`): below zero
   * it takes a deal short of even.
   */
  bar: number;
  /** How far short of its bar it still counters, as the same share; null: it never does (`BotStyle.haggle`). */
  haggle: number | null;
  /** Weekly points a player it drafted is worth to it beyond his worth, by its loyalty (`BotStyle.draftPremium`). */
  draftPremium: number;
  /** It drafted this player of its own. */
  drafted: (playerId: string) => boolean;
  /**
   * Whether the host can read what a draft pick is worth now: a player at its
   * place in its pool (PLAN-2026-10-27-the-next-season §2.6 rule 7). Absent:
   * every pick's worth is read.
   */
  pickKnown?: (pickId: string) => boolean;
  rand: () => number;
  /**
   * The proposer's players a counter may ask for — none on IR, none the
   * bot's other trades name — each with his worth over the free agents at
   * his position (DIRECTION A19), whatever slot he sits in.
   */
  proposerPieces: readonly { id: string; over: number }[];
  maxPerSide: number;
  /** The fewest drops that make a roster legal after the swap. */
  dropsFor: (teamId: string, incoming: readonly string[], outgoing: readonly string[]) => string[];
  playerName: (playerId: string) => string;
  /** A designation has him out (`ValueContext.outStatus`). */
  isOut: (playerId: string) => boolean;
}): {
  delayMs: number;
  intent: TradeIntent;
  /**
   * What it weighed, for a host that records why (the bot league's log): its
   * sum, the gain its bar asked for, how far short it would still counter,
   * the deal's size and whether the screen calls it lopsided against it.
   */
  weighed: {
    gain: number;
    acceptAt: number;
    haggle: number | null;
    size: number;
    lopsidedAgainst: boolean;
  };
} {
  const { trade, readout: r } = input;
  const [from, to] = trade.sides;
  // ITS BAR IS A SHARE OF THE DEAL (PLAN-2026-10-23 §4): what it wants from a
  // trade of two stars and from one of two backups is the same in proportion,
  // where points a week made it strict on the small and loose on the large.
  const size = Math.max(r.plain.give, r.plain.get);
  const acceptAt = input.bar * size;
  const haggle = input.haggle === null ? null : input.haggle * size;
  // ITS OWN DRAFT PICKS COST IT MORE TO GIVE, by its loyalty, as they do in
  // the offers it makes (B-294): Uncle Rico asks a lot for his guys. AND ITS
  // OWN DRAFT PICKS, the players it has yet to take (§2.6): a pick it gives
  // is one of its own guys to come.
  const premium =
    (to.playerIds.filter(input.drafted).length + to.pickIds.length) * input.draftPremium;
  // ITS LINEUP'S GAIN THE SMALLER OF TWO COUNTS (B-294): as the screen shows
  // it, and with the wire filling its holes. The screen's alone counts an
  // empty slot as nothing, so a bot with no kicker took any kicker as a whole
  // kicker's points gained, though the wire would give it one for free; the
  // wire's alone would make a hole the trade opens look cheap to give. The
  // same sum its own offers judge the other side by (`planTrade`).
  const lineup = Math.min(
    r.lineup.mine.after - r.lineup.mine.before,
    input.filled.after - input.filled.before,
  );
  const gain = r.value.get - r.value.give + lineup - premium;
  const delayMs = answerDelayMs(input.rand);
  // NEVER A TRADE THE SCREEN CALLS LOPSIDED AGAINST IT, whatever its bar or
  // its lineup: a league vote vetoes on that line, and its own bots vote so.
  const lopsidedAgainst = r.value.give - r.value.get > LOPSIDED;
  const weighed = { gain, acceptAt, haggle, size, lopsidedAgainst };
  // NOT A PLAYER WHO IS OUT (B-182): his worth counts him back after a stated
  // stretch because nothing says when he will be, and a bot does not pay for
  // a return nobody can promise. Without this, a star out for the season was
  // most of his worth to it, and a bot took him for a healthy starter.
  if (from.playerIds.some(input.isOut)) {
    return {
      delayMs,
      weighed,
      intent: { type: 'decline', tradeId: trade.id, by: to.teamId, reason: 'Not while he’s hurt.' },
    };
  }
  // NOTHING FOR NOTHING, AND NO PICK IT CANNOT PRICE (the review of chunk 5):
  // with the worths unread — a preseason with no projections, a place past
  // the pool — every side of a deal is worth nothing, its bar a share of
  // nothing, and a bot gave its first-rounder for a player nobody wanted.
  const unpriced = to.pickIds.some((id) => !(input.pickKnown?.(id) ?? true));
  if (unpriced || (to.playerIds.length + to.pickIds.length > 0 && r.plain.get <= 0)) {
    return {
      delayMs,
      weighed,
      intent: {
        type: 'decline',
        tradeId: trade.id,
        by: to.teamId,
        reason: unpriced ? 'Not until I know what that pick is worth.' : 'Not for nothing.',
      },
    };
  }
  if (gain >= acceptAt && !lopsidedAgainst) {
    return {
      delayMs,
      weighed,
      intent: {
        type: 'accept',
        tradeId: trade.id,
        by: to.teamId,
        drops: input.dropsFor(to.teamId, from.playerIds, to.playerIds),
      },
    };
  }
  // A COUNTER IS ANSWERED, NOT COUNTERED AGAIN (B-296): one round of
  // haggling. Each counter asks for one more of the other side's players, so two
  // bots countering each other's counters went back and forth until the deal
  // reached the most players a side may give — seven offers in a day and a
  // half in the season suite's bot league — and a person who countered a bot
  // was countered in turn. Close enough to counter is close enough to say no.
  // A HAGGLER COUNTERS what falls short of its bar by no more than its
  // haggling reaches (A4); one that never offers a trade answers yes or no.
  if (
    haggle !== null &&
    !lopsidedAgainst &&
    acceptAt - gain <= haggle &&
    trade.counterOf === null
  ) {
    // THE LEAST THAT CLOSES THE GAP, by worth (A19): the proposer's player
    // worth just enough over the wire to bring the deal to its bar. It asked
    // for the best of the proposer's bench, whatever the gap — and a bench
    // player the wire matches, worth nothing to it, closed none. With nobody
    // who closes it, the answer is no.
    // NOR SO MUCH THAT THE SCREEN CALLS IT LOPSIDED (B-294): its ask keeps
    // the two sides within five points a week over the wire, the line a
    // league vote vetoes on.
    const need = acceptAt - gain;
    const most = LOPSIDED - (r.value.get - r.value.give);
    const extra = [...input.proposerPieces]
      .filter(
        (p) =>
          !from.playerIds.includes(p.id) && !input.isOut(p.id) && p.over >= need && p.over <= most,
      )
      .sort((x, y) => x.over - y.over)[0]?.id;
    if (extra !== undefined && from.playerIds.length + from.pickIds.length < input.maxPerSide) {
      return {
        delayMs,
        weighed,
        intent: {
          type: 'propose',
          by: to.teamId,
          to: from.teamId,
          give: [...to.playerIds],
          get: [...from.playerIds, extra],
          // THE PICKS STAY IN IT (the review of chunk 5): a counter asks for
          // one more player, and the deal it answers had these too.
          givePicks: [...to.pickIds],
          getPicks: [...from.pickIds],
          myDrops: input.dropsFor(to.teamId, [...from.playerIds, extra], to.playerIds),
          note: `Close. Throw in ${input.playerName(extra)} and we have a deal.`,
          counterOf: trade.id,
        },
      };
    }
  }
  return {
    delayMs,
    weighed,
    intent: {
      type: 'decline',
      tradeId: trade.id,
      by: to.teamId,
      reason: gain < -6 ? 'Not even close.' : 'Not this week.',
    },
  };
}

/**
 * In a league vote, the other managers vote over the window: a lopsided trade
 * draws vetoes, a fair one few, and half the league never bothers. Bots alone
 * can never reach the threshold, so a league of one person and bots is not
 * vetoed by robots (TRADES.md "Review and veto"). Each voter brings its own
 * seeded generator; the draws are in the order the simulator always made them.
 */
export function botVotes(input: {
  trade: Trade;
  lopsided: boolean;
  needed: number;
  windowHours: number;
  voters: readonly { teamId: string; isBot: boolean; rand: () => number }[];
}): { teamId: string; delayMs: number; veto: boolean }[] {
  const out: { teamId: string; delayMs: number; veto: boolean }[] = [];
  let botVetoes = 0;
  for (const v of input.voters) {
    const veto = v.rand() < (input.lopsided ? 0.7 : 0.1);
    if (veto && v.isBot) {
      if (botVetoes + 1 >= input.needed) continue;
      botVetoes++;
    }
    if (!veto && v.rand() < 0.5) continue;
    out.push({
      teamId: v.teamId,
      delayMs: (1 + Math.round(v.rand() * Math.max(1, input.windowHours - 2))) * H,
      veto,
    });
  }
  return out;
}

/** "Lopsided": the value lines differ by more than five points a week. */
/** Points a week over the wire, one side's over the other's, past which a trade is lopsided. */
const LOPSIDED = 5;

export function isLopsided(r: TradeReadout): boolean {
  return Math.abs(r.value.get - r.value.give) > LOPSIDED;
}

/**
 * A generator seeded by a string, for a host with no seed of its own: the
 * league store, where a bot's answer has to be the same answer however many
 * times the clock asks (FNV-1a into mulberry32). The simulator keeps its own,
 * seeded by its world.
 */
export function seededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The generator a real league's bot answers an offer with: the offer's own. */
export function answerRandom(tradeId: string): () => number {
  return seededRandom(`answer|${tradeId}`);
}

/**
 * How long a bot takes to answer an offer, from the generator's first draw:
 * fifteen minutes to an hour (B-182, DIRECTION A20). It was one to nine
 * hours, which left a manager setting a lineup around a trade waiting most
 * of a day, and each counter as long again; inside the hour a negotiation is
 * still a conversation, and never an instant one. One copy for both hosts:
 * `botAnswer`'s delay, which the simulator plans by, and `botAnswersAt`.
 */
export function answerDelayMs(rand: () => number): number {
  return (15 + Math.round(rand() * 45)) * MIN;
}

/**
 * When a bot answers an offer made to it in a real league: the delay
 * `botAnswer` draws first from `answerRandom` (`answerDelayMs`), so the
 * clock can plan the instant from the offer alone, without the league.
 */
export function botAnswersAt(trade: Pick<Trade, 'id' | 'createdAt'>): number {
  return trade.createdAt + answerDelayMs(answerRandom(trade.id));
}

/**
 * When a real league's bots vote on a trade in a league vote: halfway through
 * the window, together. The simulator spreads them over the window; the store
 * plans one instant per trade rather than one per bot, and who votes and how
 * is `botVotes` either way.
 */
export function botsVoteAt(trade: Pick<Trade, 'reviewEndsAt'>, windowHours: number): number | null {
  return trade.reviewEndsAt === null ? null : trade.reviewEndsAt - (windowHours * H) / 2;
}
